import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Avatar,
  Button,
  Card,
  Divider,
  Dropdown,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  DownOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { PageContainer, PageHeader, DevFilePathBadge, MarkdownDisplay, RichTextEditor, withDevModalFilePath } from '../../components/common';
import { PAGE_MEMO_PAGES } from '../../constants/pageMemoPages';
import { trpc } from '../../lib/trpc';
import { usePermission } from '../../hooks/usePermission';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatDateWithFallback } from '../../utils/dateFormat';
import { PAGE_MEMO_REACTION_EMOJIS } from '@shared/types/page-memo.types';
import type { PageMemoBundle, PageMemoSummary } from '@shared/types/page-memo.types';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/settings/PageMemoSettingsPage.tsx');

const TEMPLATES = [
  {
    key: 'basic',
    label: '基本テンプレート',
    content: [
      '## 目的',
      '',
      '## 関連リンク',
      '- ',
      '',
      '## 対応フロー',
      '1. ',
      '2. ',
    ].join('\n'),
  },
  {
    key: 'manual',
    label: 'マニュアル案内',
    content: [
      '## 作業前の注意',
      '- ',
      '',
      '## 手順',
      '1. ',
      '2. ',
      '',
      '## 問い合わせ先',
      '- ',
    ].join('\n'),
  },
  {
    key: 'link',
    label: 'リンク集',
    content: [
      '## 関連ドキュメント',
      '- [リンク名](https://example.com)',
      '',
      '## 外部ルール',
      '- [リンク名](https://example.com)',
    ].join('\n'),
  },
];

const templateMenuItems: MenuProps['items'] = TEMPLATES.map((template) => ({
  key: template.key,
  label: template.label,
}));

export const PageMemoSettingsPage = (): JSX.Element => {
  const { message } = App.useApp();
  const utils = trpc.useUtils();
  const { isManagerOrAdmin } = usePermission();
  const [selectedPageKey, setSelectedPageKey] = useState<string>(PAGE_MEMO_PAGES[0]?.key ?? '');
  const [draft, setDraft] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [personalDraft, setPersonalDraft] = useState('');
  const [isPersonalEditing, setIsPersonalEditing] = useState(false);

  const selectedPage = useMemo(
    () => PAGE_MEMO_PAGES.find((page) => page.key === selectedPageKey),
    [selectedPageKey]
  );

  const { data: summaries, isLoading: summariesLoading, refetch: refetchSummaries } =
    trpc.pageMemo.listSummaries.useQuery();

  const hasSelection = Boolean(selectedPageKey);
  const { data, isLoading, isFetching, refetch } = trpc.pageMemo.getPageMemo.useQuery(
    { pageKey: selectedPageKey },
    { enabled: hasSelection }
  );
  const memoBundle = data as PageMemoBundle | undefined;

  const upsertMutation = trpc.pageMemo.upsertCommonMemo.useMutation({
    onSuccess: async () => {
      message.success('共通メモを保存しました');
      if (selectedPageKey) {
        await utils.pageMemo.getPageMemo.invalidate({ pageKey: selectedPageKey });
      }
      await utils.pageMemo.listSummaries.invalidate();
      void refetch();
      setIsEditOpen(false);
    },
    onError: (error) => {
      message.error(error.message || '共通メモの保存に失敗しました');
    },
  });

  const upsertPersonalMutation = trpc.pageMemo.upsertPersonalMemo.useMutation({
    onSuccess: async () => {
      message.success('個人メモを保存しました');
      if (selectedPageKey) {
        await utils.pageMemo.getPageMemo.invalidate({ pageKey: selectedPageKey });
      }
      await utils.pageMemo.listSummaries.invalidate();
      void refetch();
      setIsPersonalEditing(false);
    },
    onError: (error) => {
      message.error(error.message || '個人メモの保存に失敗しました');
    },
  });

  const summaryList = useMemo<PageMemoSummary[]>(
    () => (Array.isArray(summaries) ? (summaries as PageMemoSummary[]) : []),
    [summaries]
  );
  const summaryMap = useMemo(() => {
    const map = new Map<string, PageMemoSummary>();
    summaryList.forEach((item) => map.set(item.pageKey, item));
    return map;
  }, [summaryList]);

  const tableData = useMemo(
    () =>
      PAGE_MEMO_PAGES.map((page) => {
        const summary = summaryMap.get(page.key);
        return {
          pageKey: page.key,
          label: page.label,
          hasCommonMemo: summary?.hasCommonMemo ?? false,
          commentCount: summary?.commentCount ?? 0,
          reactionCount: summary?.reactionCount ?? 0,
          reactions: summary?.reactions ?? [],
          commonUpdatedAt: summary?.commonUpdatedAt ?? null,
          hasPersonalMemo: summary?.hasPersonalMemo ?? false,
          personalUpdatedAt: summary?.personalUpdatedAt ?? null,
        };
      }),
    [summaryMap]
  );

  const renderReactionBadges = (reactions: (typeof tableData)[number]['reactions']) => {
    const reactionMap = new Map(reactions.map((item) => [item.emoji, item.count]));
    const items = PAGE_MEMO_REACTION_EMOJIS
      .map((emoji) => ({
        emoji,
        count: reactionMap.get(emoji) ?? 0,
      }))
      .filter((item) => item.count > 0);

    if (items.length === 0) {
      return <Text type="secondary">なし</Text>;
    }

    return (
      <Space size={4} wrap>
        {items.map((item) => {
          return (
            <Tag key={item.emoji} color="blue">
              {item.emoji} {item.count}
            </Tag>
          );
        })}
      </Space>
    );
  };

  const columns: ColumnsType<(typeof tableData)[number]> = [
    {
      title: 'ページ',
      dataIndex: 'label',
      key: 'label',
      render: (_: string, record) => (
        <Link
          to={record.pageKey}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Button type="link" style={{ padding: 0, height: 'auto' }}>
            {record.label}
          </Button>
        </Link>
      ),
    },
    {
      title: 'メモ',
      dataIndex: 'hasCommonMemo',
      key: 'hasCommonMemo',
      width: 90,
      render: (value: boolean) => (
        value ? <Tag color="green">あり</Tag> : <Tag color="default">なし</Tag>
      ),
    },
    {
      title: 'コメント',
      dataIndex: 'commentCount',
      key: 'commentCount',
      width: 90,
      render: (value: number, record) => (
        <Button
          type="link"
          onClick={(event) => {
            event.stopPropagation();
            handleOpenComments(record.pageKey);
          }}
        >
          {value}
        </Button>
      ),
    },
    {
      title: '個人メモ',
      dataIndex: 'hasPersonalMemo',
      key: 'hasPersonalMemo',
      width: 90,
      render: (value: boolean) => (
        value ? <Tag color="blue">あり</Tag> : <Tag color="default">なし</Tag>
      ),
    },
    {
      title: 'リアクション',
      dataIndex: 'reactions',
      key: 'reactionCount',
      width: 220,
      render: (value: (typeof tableData)[number]['reactions']) => renderReactionBadges(value),
    },
    {
      title: '更新',
      dataIndex: 'commonUpdatedAt',
      key: 'commonUpdatedAt',
      width: 140,
      render: (value: Date | null) =>
        formatDateWithFallback(value, { showTime: true }),
    },
    ...(isManagerOrAdmin ? [
      {
        title: '操作',
        key: 'actions',
        width: 110,
        render: (_: unknown, record: (typeof tableData)[number]) => (
          <Button
            type="link"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenEdit(record.pageKey);
            }}
          >
            編集
          </Button>
        ),
      },
    ] : []),
  ];

  useEffect(() => {
    setDraft(memoBundle?.commonMemo?.content ?? '');
  }, [memoBundle?.commonMemo?.content, selectedPageKey]);

  useEffect(() => {
    if (!isPersonalEditing) {
      setPersonalDraft(memoBundle?.personalMemo?.content ?? '');
    }
  }, [memoBundle?.personalMemo?.content, isPersonalEditing, selectedPageKey]);

  const handleSave = () => {
    if (!selectedPageKey) return;
    upsertMutation.mutate({
      pageKey: selectedPageKey,
      content: draft,
    });
  };

  const handleSavePersonal = () => {
    if (!selectedPageKey) return;
    upsertPersonalMutation.mutate({
      pageKey: selectedPageKey,
      content: personalDraft,
    });
  };

  const handleApplyTemplate: MenuProps['onClick'] = ({ key }) => {
    const template = TEMPLATES.find((item) => item.key === key);
    if (!template) return;
    setDraft((prev) => (prev ? `${prev}\n\n${template.content}` : template.content));
  };

  const handleOpenEdit = (pageKey: string) => {
    setSelectedPageKey(pageKey);
    setIsEditOpen(true);
  };

  const handleOpenComments = (pageKey: string) => {
    setSelectedPageKey(pageKey);
    setIsCommentOpen(true);
  };

  const handleCloseEdit = () => {
    setDraft(memoBundle?.commonMemo?.content ?? '');
    setIsEditOpen(false);
  };

  const handleCancelPersonalEdit = () => {
    setPersonalDraft(memoBundle?.personalMemo?.content ?? '');
    setIsPersonalEditing(false);
  };

  const handleCloseComments = () => {
    setIsCommentOpen(false);
  };

  const isDataForSelectedPage = memoBundle?.pageKey === selectedPageKey;
  const comments = isDataForSelectedPage ? (memoBundle?.comments ?? []) : [];
  const selectedReactionMap = useMemo<Map<string, number>>(() => {
    if (!isDataForSelectedPage) {
      return new Map<string, number>();
    }
    return new Map<string, number>(
      memoBundle?.reactions?.map((reaction) => [reaction.emoji, reaction.count]) ?? []
    );
  }, [memoBundle?.reactions, isDataForSelectedPage]);

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/settings/PageMemoSettingsPage.tsx" />
      <PageHeader
        title="一元メモ設定"
        subtitle="各ページに表示する共通メモを編集できます"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                void refetch();
                void refetchSummaries();
              }}
            />
          </Space>
        }
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card title="ページ一覧" loading={summariesLoading}>
          <Table
            rowKey="pageKey"
            columns={columns}
            dataSource={tableData}
            pagination={{ pageSize: 10 }}
            size="middle"
            rowClassName={(record) =>
              record.pageKey === selectedPageKey ? 'page-memo-row-selected' : ''
            }
            onRow={(record) => ({
              onClick: () => setSelectedPageKey(record.pageKey),
            })}
          />
        </Card>

        <Card loading={isLoading || isFetching} title="現在の共通メモ">
          {memoBundle?.commonMemo?.content ? (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <MarkdownDisplay content={memoBundle.commonMemo.content} />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary">
                最終更新: {formatDateWithFallback(memoBundle.commonMemo.updatedAt, { showTime: true })}
              </Text>
            </Space>
          ) : (
            <Text type="secondary">まだ共通メモはありません。</Text>
          )}
        </Card>

        <Card
          loading={isLoading || isFetching}
          title="現在の個人メモ"
          extra={
            isPersonalEditing ? (
              <Space size={8}>
                <Button
                  type="primary"
                  loading={upsertPersonalMutation.isPending}
                  onClick={handleSavePersonal}
                >
                  保存
                </Button>
                <Button onClick={handleCancelPersonalEdit}>キャンセル</Button>
              </Space>
            ) : (
              <Button onClick={() => setIsPersonalEditing(true)}>編集</Button>
            )
          }
        >
          {isPersonalEditing ? (
            <RichTextEditor
              value={personalDraft}
              onChange={setPersonalDraft}
              minHeight={200}
              showToolbar
            />
          ) : memoBundle?.personalMemo?.content ? (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <MarkdownDisplay content={memoBundle.personalMemo.content} />
              <Divider style={{ margin: '12px 0' }} />
              <Text type="secondary">
                最終更新: {formatDateWithFallback(memoBundle.personalMemo.updatedAt, { showTime: true })}
              </Text>
            </Space>
          ) : (
            <Text type="secondary">まだ個人メモはありません。</Text>
          )}
        </Card>
      </Space>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .page-memo-row-selected td {
              background: rgba(24, 144, 255, 0.08) !important;
            }
          `,
        }}
      />

      <Modal
        title={`共通メモ編集${selectedPage ? `: ${selectedPage.label}` : ''}`}
        open={isEditOpen}
        onCancel={handleCloseEdit}
        onOk={handleSave}
        okText="保存"
        cancelText="閉じる"
        okButtonProps={{
          loading: upsertMutation.isPending,
          disabled: !hasSelection || !isManagerOrAdmin,
        }}
        width={720}
        destroyOnHidden
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap>
            <Text strong>対象ページ</Text>
            <Text>{selectedPage?.label ?? '未選択'}</Text>
            <Text type="secondary">{selectedPageKey || '-'}</Text>
            <Dropdown menu={{ items: templateMenuItems, onClick: handleApplyTemplate }}>
              <Button>
                テンプレート挿入 <DownOutlined />
              </Button>
            </Dropdown>
          </Space>

          <Text type="secondary">
            Markdown形式で入力できます。リンク埋め込みは `[表示名](https://...)` を使用してください。
          </Text>

          <RichTextEditor
            value={draft}
            onChange={setDraft}
            minHeight={240}
            showToolbar
          />

          <Card title="プレビュー" size="small">
            {draft ? (
              <MarkdownDisplay content={draft} />
            ) : (
              <Text type="secondary">まだ共通メモはありません。</Text>
            )}
          </Card>
        </Space>
      </Modal>

      <Modal
        title={`コメント一覧${selectedPage ? `: ${selectedPage.label}` : ''}`}
        open={isCommentOpen}
        onCancel={handleCloseComments}
        footer={null}
        width={720}
        destroyOnHidden
      >
        <Spin spinning={isLoading || isFetching}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title="リアクション">
              <Space size={4} wrap>
                {PAGE_MEMO_REACTION_EMOJIS
                  .map((emoji) => ({
                    emoji,
                    count: selectedReactionMap.get(emoji) ?? 0,
                  }))
                  .filter((item) => item.count > 0)
                  .map((item) => (
                    <Tag key={item.emoji} color="blue">
                      {item.emoji} {item.count}
                    </Tag>
                  ))}
                {PAGE_MEMO_REACTION_EMOJIS.every((emoji) => (selectedReactionMap.get(emoji) ?? 0) === 0) && (
                  <Text type="secondary">なし</Text>
                )}
              </Space>
            </Card>

            <Card
              size="small"
              title={`コメント${comments.length > 0 ? ` (${comments.length})` : ''}`}
            >
              {comments.length === 0 ? (
                <Empty description="コメントはまだありません" />
              ) : (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {comments.map((comment) => {
                    const displayName = comment.user?.name || comment.user?.email || '不明なユーザー';
                    return (
                      <Card key={comment.id} size="small" style={{ background: '#fafafa' }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          <Space size={8} align="center">
                            <Avatar
                              size={32}
                              src={comment.user?.avatarUrl ?? undefined}
                              icon={!comment.user?.avatarUrl && <UserOutlined />}
                            />
                            <Space direction="vertical" size={0}>
                              <Text>{displayName}</Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {formatDateWithFallback(comment.createdAt, { showTime: true })}
                              </Text>
                            </Space>
                          </Space>
                          <Text style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Text>
                        </Space>
                      </Card>
                    );
                  })}
                </Space>
              )}
            </Card>
          </Space>
        </Spin>
      </Modal>
    </PageContainer>
  );
};
